import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Eye, Cow, DownloadSimple, FilePdf, Warning, Receipt, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import EmitirRECDialog from "@/components/rec/EmitirRECDialog";
import RECDetalleDialog from "@/components/rec/RECDetalleDialog";
import ContingenciaRECDialog from "@/components/rec/ContingenciaRECDialog";

const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "-");
const fmtKg = (n) => new Intl.NumberFormat("es-AR", { maximumFractionDigits: 3 }).format(n || 0) + " kg";

export default function RECEmitidosTab({ config }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emitirOpen, setEmitirOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterEspecie, setFilterEspecie] = useState("__all__");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterEspecie !== "__all__") params.especie = filterEspecie;
      const { data } = await api.get("/rec", { params });
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterEspecie]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((it) =>
      (it.cliente_nombre || "").toLowerCase().includes(s) ||
      (it.numero_formateado || "").includes(s) ||
      (it.cuit_receptor || "").includes(s) ||
      (it.cod_remito || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const [contingenciaRec, setContingenciaRec] = useState(null);

  const verPDF = async (rec) => {
    try {
      const { data } = await api.get(`/rec/${rec.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    }
  };

  const descargarPDF = async (rec) => {
    try {
      const { data } = await api.get(`/rec/${rec.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `REC_${rec.numero_formateado || rec.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("No se pudo descargar el PDF");
    }
  };

  const abrirDetalle = (rec) => { setSelected(rec); setDetalleOpen(true); };

  const sincronizarEstado = async (rec) => {
    try {
      const { data } = await api.post(`/rec/${rec.id}/sincronizar-estado`);
      if (data.anulado) {
        toast.success(`REC ${rec.numero_formateado} sincronizado: AFIP lo tiene como ANULADO`);
      } else {
        toast.success(`REC ${rec.numero_formateado}: estado actual en AFIP = ${data.estado || "EMI"}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo sincronizar el estado con AFIP");
    }
  };

  const [importing, setImporting] = useState(false);
  const importarAfip = async () => {
    const pe = config?.punto_emision;
    if (!pe) { toast.error("No hay punto de emisión configurado"); return; }
    if (!confirm(`¿Importar desde AFIP todos los REC del punto de emisión ${pe}?`)) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/rec/importar-desde-afip?punto_emision=${pe}`, null, { timeout: 180000 });
      toast.success(`Importación completa · Nuevos: ${data.nuevos} · Actualizados: ${data.actualizados}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al importar desde AFIP");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 gap-3 flex-wrap">
        <CardTitle>REC emitidos ({filtered.length})</CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Buscar…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-52"
            data-testid="rec-search-input"
          />
          <Select value={filterEspecie} onValueChange={setFilterEspecie}>
            <SelectTrigger className="w-40" data-testid="rec-filter-especie">
              <SelectValue placeholder="Especie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas</SelectItem>
              <SelectItem value="bovino">Bovino</SelectItem>
              <SelectItem value="porcino">Porcino</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={importarAfip} disabled={importing} data-testid="rec-import-afip-btn">
            <DownloadSimple size={16} className="mr-1" /> {importing ? "Importando…" : `Importar de AFIP (PE ${config?.punto_emision || "-"})`}
          </Button>
          <Button size="sm" onClick={() => setEmitirOpen(true)} data-testid="emitir-rec-btn">
            <Plus size={16} className="mr-1" /> Emitir REC
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center text-neutral-500 py-8">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-neutral-400 py-12" data-testid="rec-empty">
            <Cow size={40} weight="duotone" className="mx-auto mb-3" />
            No hay REC emitidos aún. Emití el primero con "Emitir REC".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-neutral-500 border-b">
                <tr>
                  <th className="text-left py-2">Nº</th>
                  <th className="text-left">Fecha</th>
                  <th className="text-left">Especie</th>
                  <th className="text-left">Cliente</th>
                  <th className="text-right">Kilos</th>
                  <th className="text-left">Factura</th>
                  <th className="text-left">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((rec) => {
                  const esAnulado = rec.anulado || String(rec.estado || "").toUpperCase() === "ANU";
                  return (
                  <tr key={rec.id} className={`border-b hover:bg-neutral-50 ${esAnulado ? "opacity-70" : ""}`} data-testid={`rec-row-${rec.id}`}>
                    <td className="py-2 font-mono">{rec.numero_formateado}</td>
                    <td>{fmtDate(rec.fecha_emision)}</td>
                    <td className="capitalize">{rec.especie}</td>
                    <td>{rec.cliente_nombre}</td>
                    <td className="text-right font-mono">{fmtKg(rec.total_kilos)}</td>
                    <td className="font-mono">{rec.factura_numero || <span className="text-neutral-400">-</span>}</td>
                    <td>
                      {esAnulado ? (
                        <Badge className="bg-red-600 text-white border-red-700 font-bold tracking-wide" data-testid={`rec-anulado-${rec.id}`}>
                          ANULADO
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          {rec.estado || "AUTORIZADO"}
                        </Badge>
                      )}
                    </td>
                    <td className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => abrirDetalle(rec)} title="Ver detalle">
                        <Eye size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => verPDF(rec)} title="Ver PDF" data-testid={`rec-ver-pdf-${rec.id}`}>
                        <FilePdf size={16} className="text-red-700" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => descargarPDF(rec)} title="Descargar PDF" data-testid={`rec-descargar-pdf-${rec.id}`}>
                        <DownloadSimple size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => sincronizarEstado(rec)} title="Sincronizar estado con AFIP" data-testid={`rec-sync-${rec.id}`}>
                        <ArrowsClockwise size={16} className="text-blue-600" />
                      </Button>
                      {!esAnulado && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/ventas?rec_id=${rec.id}`)}
                          title="Facturar este REC (pre-carga la venta)"
                          data-testid={`rec-facturar-${rec.id}`}
                        >
                          <Receipt size={16} className="text-emerald-700" />
                        </Button>
                      )}
                      {!esAnulado && (
                        <Button size="sm" variant="ghost" onClick={() => setContingenciaRec(rec)} title="Informar contingencia" data-testid={`rec-contingencia-${rec.id}`}>
                          <Warning size={16} className="text-amber-600" />
                        </Button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <EmitirRECDialog
        open={emitirOpen}
        onOpenChange={setEmitirOpen}
        config={config}
        onSuccess={() => { setEmitirOpen(false); load(); }}
      />

      <RECDetalleDialog
        open={detalleOpen}
        onOpenChange={setDetalleOpen}
        rec={selected}
      />

      <ContingenciaRECDialog
        rec={contingenciaRec}
        onOpenChange={(v) => { if (!v) setContingenciaRec(null); }}
        onSuccess={() => { setContingenciaRec(null); load(); }}
      />
    </Card>
  );
}
